import { msg } from "@lingui/core/macro";
import { createRoute, useParams } from "@tanstack/solid-router";
import { Link } from "@/lib/u-router";
import { appRoute } from "./route";
import { createSignal, For, Show } from "solid-js";
import { AuthPlaceholder } from "@/components/auth-placeholder";
import { PageContainer } from "@/components/ui/page-container";
import { ArrowLeft, Loader2, Mail, UserPlus, Users } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TextField } from "@/components/ui/text-field";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { Separator } from "@/components/ui/separator";
import { useOrganizationMembers } from "@/features/organizations/hooks/use-organization-members";
import { api, createApiQuery } from "@/lib/api";
import {
  ACADEMY_INVITE_ROLES,
  type components,
  MODALITY_IN_PERSON_KEY,
  type OrganizationRole,
  queryKeys,
} from "@space/shared";
import { Select } from "@/components/ui/select";
import { useI18n } from "@/providers/i18n-provider";
import { OrganizationMemberRow } from "@/features/organizations/components/organization-member-row";
import { ChangeRoleDialog } from "@/features/organizations/components/change-role-dialog";
import { Tabs, TabsList, TabsPanel, TabsPanels, TabsTrigger } from "@/components/ui/tabs";
import { OrgSettingsTab } from "@/features/academy/components/org-settings-tab";
import { GradingModelsManager } from "@/features/academy/components/grading-models-manager";
import { CampusesTab } from "@/features/academy/components/campuses-tab";
// ── AUTONOMY: gestión completa de miembros + invitaciones emitidas ──
import { useCampuses } from "@/features/academy/hooks/use-academy-data";
import { useSession } from "@/lib/auth";
import { FeatureGate } from "@/features/flags/feature-gate";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";

function OrganizationDetailPage() {
  const { t } = useI18n();
  const params = useParams({ from: "/u/$u/organizations/$orgId" });
  const [email, setEmail] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [tab, setTab] = createSignal("members");
  const [inviteRole, setInviteRole] = createSignal<OrganizationRole>("student");
  // §L1 · online-first: la pestaña «Sedes» sigue el estado de la vertical
  // presencial en ESTA organización (no en la activa del switcher).
  const inPersonState = useFeatureEnabled(MODALITY_IN_PERSON_KEY, {
    orgId: () => params().orgId,
  });
  const inPersonEnabled = () => inPersonState().enabled;

  const roleLabel = (r: OrganizationRole): string => {
    const map: Record<string, string> = {
      org_admin: t(msg`Organization admin`),
      campus_admin: t(msg`Campus admin`),
      teacher: t(msg`Teacher`),
      staff: t(msg`Staff`),
      student: t(msg`Student`),
      guardian: t(msg`Guardian`),
    };
    return map[r] ?? r;
  };

  const orgs = createApiQuery<
    components["schemas"]["OrganizationDTO"][] | null,
    components["schemas"]["ErrorModel"]
  >(
    () => queryKeys.auth.organizations(),
    async () => api.GET("/api/org/listOrganizations", {}),
  );

  const org = () => orgs.data?.find((o) => o.id === params().orgId);

  const {
    members,
    isLoading,
    removeMember,
    updateMemberRole,
    updateMemberCampus,
    inviteMemberWithCampus,
    orgInvitations,
    revokeInvitation,
  } = useOrganizationMembers(() => params().orgId);

  // ── AUTONOMY: rol del actor (decide qué gestión se ofrece) + sedes ──
  const session = useSession();
  const myRole = () => members().find((m) => m.userId === session.userId())?.role;
  const canManageMembers = () => ["owner", "admin", "org_admin"].includes(myRole() ?? "");
  const actorIsOwner = () => myRole() === "owner";
  const campusesQuery = useCampuses(() => params().orgId);
  const campusOptions = () =>
    (campusesQuery.data ?? []).map((c) => ({ id: c.id, name: c.name ?? c.id }));
  const [inviteCampus, setInviteCampus] = createSignal("");
  // Diálogo de rol: UNA instancia a nivel de página (las filas se re-montan
  // con cada refetch de miembros; un diálogo por fila moriría al abrirse).
  const [roleTarget, setRoleTarget] = createSignal<{
    id: string;
    role: string;
    userId: string;
    // D3: el correo puede venir redactado por capacidad → se cae al userId.
    user: { name: string | null; email?: string | null };
  } | null>(null);

  const handleInvite = async (e: Event) => {
    e.preventDefault();
    const mail = email().trim();
    if (!mail) {
      return;
    }

    setIsSubmitting(true);
    try {
      await inviteMemberWithCampus(mail, inviteRole(), inviteCampus() || null);
      setEmail("");
      showToast(t(msg`Invitation sent`), "success");
    } catch (err) {
      console.error("Error inviting member:", err);
      showToast(t(msg`Error sending invitation`), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeRole = async (memberId: string, role: OrganizationRole) => {
    try {
      await updateMemberRole(memberId, role);
      showToast(t(msg`Role updated`), "success");
    } catch (err) {
      console.error("Error updating role:", err);
      showToast(t(msg`Could not update the role`), "error");
    }
  };

  const handleChangeCampus = async (memberId: string, campusId: string | null) => {
    try {
      await updateMemberCampus(memberId, campusId);
      showToast(t(msg`Campus updated`), "success");
    } catch (err) {
      console.error("Error updating campus:", err);
      showToast(t(msg`Could not update the campus`), "error");
    }
  };

  // Definido FUERA del JSX (scope no tracked): un arrow async inline en un
  // tracked scope dispara solid/reactivity.
  const handleRoleDialogChange = async (role: OrganizationRole) => {
    const target = roleTarget();
    if (target) {
      await handleChangeRole(target.id, role);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      await revokeInvitation(invitationId);
      showToast(t(msg`Invitation revoked`), "success");
    } catch (err) {
      console.error("Error revoking invitation:", err);
      showToast(t(msg`Could not revoke the invitation`), "error");
    }
  };

  const handleRemove = async (memberId: string) => {
    try {
      await removeMember(memberId);
      showToast(t(msg`Member removed`), "success");
    } catch (err) {
      console.error("Error removing member:", err);
      showToast(t(msg`Error removing member`), "error");
    }
  };

  return (
    <AuthPlaceholder
      title={t(msg`Organization Detail`)}
      description={t(msg`Sign in to view organization details and collaborate with members.`)}
    >
      <PageContainer maxWidth="5xl" class="space-y-8">
        <Show
          when={!orgs.isLoading}
          fallback={
            <div class="space-y-8">
              <div class="flex items-center gap-4">
                <Skeleton class="h-8 w-8 rounded-md" />
                <Skeleton class="h-8 w-48" />
              </div>
              <div class="grid gap-6 md:grid-cols-2">
                <Skeleton class="h-64 w-full rounded-xl" />
                <Skeleton class="h-64 w-full rounded-xl" />
              </div>
            </div>
          }
        >
          <Show
            when={org()}
            fallback={
              <div class="flex flex-col items-center justify-center py-20 text-center space-y-4">
                <div class="h-20 w-20 rounded-full bg-muted flex items-center justify-center">
                  <Users class="h-10 w-10 text-muted-foreground" />
                </div>
                <h1 class="text-2xl font-bold">{t(msg`Organization not found`)}</h1>
                <Link to="/organizations">
                  <Button variant="outline">{t(msg`Back to Organizations`)}</Button>
                </Link>
              </div>
            }
          >
            {(o) => (
              <>
                <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div class="space-y-1">
                    <div class="flex items-center gap-3">
                      <Link to="/organizations">
                        <Button variant="ghost" size="icon" class="h-8 w-8">
                          <ArrowLeft class="h-4 w-4" />
                        </Button>
                      </Link>
                      <h1 class="text-3xl font-bold tracking-tight">{o().name}</h1>
                    </div>
                    <div class="flex items-center gap-2 pl-11">
                      <Badge variant="secondary" class="text-[10px] uppercase tracking-wider">
                        @{o().slug}
                      </Badge>
                      <span class="text-xs text-muted-foreground">
                        {(() => {
                          const { id } = o();
                          return t(msg`ID: ${id}`);
                        })()}
                      </span>
                    </div>
                  </div>
                </header>

                <Tabs value={tab()} onChange={setTab}>
                  <TabsList>
                    <TabsTrigger value="members">{t(msg`Members`)}</TabsTrigger>
                    <TabsTrigger value="settings">{t(msg`Settings`)}</TabsTrigger>
                    <TabsTrigger value="grading-models">{t(msg`Grading models`)}</TabsTrigger>
                    {/* §L1 · online-first: la pestaña de sedes solo aparece si la
                        academia usa lo presencial. El panel además va gateado,
                        por si se llega con `?tab=campuses` en la URL. */}
                    <Show when={inPersonEnabled()}>
                      <TabsTrigger value="campuses">{t(msg`Campuses`)}</TabsTrigger>
                    </Show>
                  </TabsList>
                  <TabsPanels
                    value={tab()}
                    order={["members", "settings", "grading-models", "campuses"]}
                  >
                    <TabsPanel value="members">
                      <div class="grid gap-6 pt-4 md:grid-cols-2">
                        {/* Miembros */}
                        <Card>
                          <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle class="text-lg font-bold">{t(msg`Members`)}</CardTitle>
                            <Users class="h-4 w-4 text-muted-foreground" />
                          </CardHeader>
                          <CardContent>
                            <div class="space-y-4">
                              <For
                                each={members()}
                                fallback={
                                  <Show when={!isLoading()}>
                                    <div class="py-10 text-center text-sm text-muted-foreground">
                                      {t(msg`No members yet`)}
                                    </div>
                                  </Show>
                                }
                              >
                                {(member) => (
                                  <OrganizationMemberRow
                                    member={member}
                                    campuses={campusOptions()}
                                    canManage={canManageMembers()}
                                    actorIsOwner={actorIsOwner()}
                                    onRemove={handleRemove}
                                    onRequestChangeRole={(m) => setRoleTarget(m)}
                                    onChangeCampus={handleChangeCampus}
                                  />
                                )}
                              </For>
                              <Show when={isLoading() && members().length === 0}>
                                <For each={Array.from({ length: 3 })}>
                                  {() => <Skeleton class="h-12 w-full rounded-md" />}
                                </For>
                              </Show>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Invitaciones / Acciones */}
                        <Card>
                          <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle class="text-lg font-bold">
                              {t(msg`Invite members`)}
                            </CardTitle>
                            <Mail class="h-4 w-4 text-muted-foreground" />
                          </CardHeader>
                          <CardContent class="space-y-6">
                            <form onSubmit={handleInvite} class="space-y-4">
                              <div class="space-y-2">
                                <TextField
                                  type="email"
                                  placeholder={t(msg`email@example.com`)}
                                  value={email()}
                                  onInput={(e) => setEmail(e.currentTarget.value)}
                                  disabled={isSubmitting()}
                                />
                              </div>
                              <div class="space-y-2">
                                <Select<OrganizationRole>
                                  aria-label={t(msg`Role`)}
                                  value={inviteRole()}
                                  onChange={setInviteRole}
                                  options={ACADEMY_INVITE_ROLES.map((r) => ({
                                    value: r,
                                    label: roleLabel(r),
                                  }))}
                                />
                              </div>
                              {/* AUTONOMY: sede pre-vinculada a la invitación. */}
                              <Show when={campusOptions().length > 0}>
                                <div class="space-y-2">
                                  <Select
                                    aria-label={t(msg`Campus`)}
                                    value={inviteCampus()}
                                    onChange={setInviteCampus}
                                    options={[
                                      { value: "", label: t(msg`All campuses`) },
                                      ...campusOptions().map((c) => ({
                                        value: c.id,
                                        label: c.name,
                                      })),
                                    ]}
                                  />
                                </div>
                              </Show>
                              <Button
                                type="submit"
                                class="w-full gap-2"
                                disabled={isSubmitting() || !email().includes("@")}
                              >
                                <Show
                                  when={isSubmitting()}
                                  fallback={
                                    <>
                                      <UserPlus class="h-4 w-4" /> {t(msg`Invite`)}
                                    </>
                                  }
                                >
                                  <Loader2 class="h-4 w-4 animate-spin" /> {t(msg`Inviting...`)}
                                </Show>
                              </Button>
                            </form>

                            <Separator />

                            <div class="space-y-2 text-center">
                              <p class="text-xs text-muted-foreground leading-relaxed">
                                {t(
                                  msg`Invited members will receive an email to join this organization.`,
                                )}
                              </p>
                            </div>

                            {/* ── AUTONOMY: invitaciones emitidas (ver + revocar) ── */}
                            <Show when={canManageMembers()}>
                              <Separator />
                              <div class="space-y-2" data-testid="org-pending-invitations">
                                <h3 class="text-sm font-bold">{t(msg`Pending invitations`)}</h3>
                                <For
                                  each={orgInvitations().filter((i) => i.status === "pending")}
                                  fallback={
                                    <p class="text-xs text-muted-foreground">
                                      {t(msg`No pending invitations.`)}
                                    </p>
                                  }
                                >
                                  {(invitation) => (
                                    <div class="flex items-center justify-between gap-2 rounded-lg border border-border/40 px-3 py-2">
                                      <div class="min-w-0">
                                        <p class="truncate text-sm">{invitation.email}</p>
                                        <p class="truncate text-xs text-muted-foreground capitalize">
                                          {invitation.role.replace("_", " ")}
                                        </p>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        class="text-destructive"
                                        onClick={() => {
                                          void handleRevokeInvitation(invitation.id);
                                        }}
                                      >
                                        {t(msg`Revoke`)}
                                      </Button>
                                    </div>
                                  )}
                                </For>
                              </div>
                            </Show>
                          </CardContent>
                        </Card>
                      </div>
                    </TabsPanel>
                    <TabsPanel value="settings">
                      <div class="pt-4">
                        <OrgSettingsTab orgId={o().id} />
                      </div>
                    </TabsPanel>
                    <TabsPanel value="grading-models">
                      <div class="pt-4">
                        <GradingModelsManager orgId={o().id} canManage={canManageMembers()} />
                      </div>
                    </TabsPanel>
                    <TabsPanel value="campuses">
                      <div class="pt-4">
                        <FeatureGate feature={MODALITY_IN_PERSON_KEY} orgId={() => o().id}>
                          <CampusesTab orgId={o().id} />
                        </FeatureGate>
                      </div>
                    </TabsPanel>
                  </TabsPanels>
                </Tabs>

                {/* AUTONOMY: diálogo de cambio de rol — instancia única SIEMPRE
                    montada (montarlo ya-abierto en el mismo click lo cerraba al
                    instante por outside-click del overlay). */}
                <ChangeRoleDialog
                  open={Boolean(roleTarget())}
                  onOpenChange={(open) => {
                    if (!open) {
                      setRoleTarget(null);
                    }
                  }}
                  memberName={
                    roleTarget()?.user.name ||
                    roleTarget()?.user.email ||
                    roleTarget()?.userId ||
                    ""
                  }
                  currentRole={roleTarget()?.role ?? ""}
                  actorIsOwner={actorIsOwner()}
                  onRoleChange={handleRoleDialogChange}
                />
              </>
            )}
          </Show>
        </Show>
      </PageContainer>
    </AuthPlaceholder>
  );
}

export const organizationDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organizations/$orgId",
  component: OrganizationDetailPage,
});
